import { makeStyles, createStyles, Theme, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import { format } from "date-fns";
import { ReactNode } from "react";

export const PaymentHistoryItemLoading = () => {
  const classes = useStyles({ color: "default" });

  return (
    <div className={classes.paymentDetails}>
      <div
        className={clsx(classes.paymentTimestamp, classes.paymentDate, classes.paymentDateLoading)}
      >
        <Skeleton width={50} className={classes.dateSkeleton} />
      </div>
      <div className={clsx(classes.paymentTimestamp, classes.paymentTime)}>
        <Skeleton width={40} />
        <Typography className={classes.status}>
          <Skeleton width={120} />
        </Typography>
      </div>
    </div>
  );
};

export const PaymentHistoryItem = ({
  date,
  status,
  dateFormat = "dd/MM/yy",
  timeFormat = "HH:mm:ss",
  color = "default",
}: PaymentHistoryItemProps) => {
  const classes = useStyles({ color });

  return (
    <div className={classes.paymentDetails}>
      <div className={clsx(classes.paymentTimestamp, classes.paymentDate)}>
        {format(date, dateFormat)}
        <div className={classes.dot} />
      </div>
      <div className={clsx(classes.paymentTimestamp, classes.paymentTime)}>
        {format(date, timeFormat)}
        <Typography className={classes.status}>{status}</Typography>
      </div>
    </div>
  );
};

export type PaymentHistoryItemProps = {
  date: Date;
  status: ReactNode;
  dateFormat?: string;
  timeFormat?: string;
  color?: "warning" | "success" | "error" | "default" | string;
};

const useStyles = makeStyles<Theme, Required<Pick<PaymentHistoryItemProps, "color">>>((theme) =>
  createStyles({
    paymentTimestamp: {
      display: "flex",
      flexDirection: "column",
      fontSize: 12,
      lineHeight: "16px",
      color: theme.palette.battleshipGrey.main,
    },
    status: {
      fontSize: 12,
      fontWeight: "bold",
      lineHeight: "19px",
      letterSpacing: 1,
      color: theme.palette.dark.main,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(2.5),
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    paymentDate: {
      flex: "1 1 auto",
      paddingRight: theme.spacing(3),
      textAlign: "right",
      marginRight: theme.spacing(2.5),
      borderRightStyle: "solid",
      borderRightWidth: 4,
      borderRightColor: "#f5f5f5",
      position: "relative",
    },
    paymentDateLoading: {
      borderRightColor: "transparent",
    },
    paymentTime: {
      flex: "1 1 100%",
      overflow: "hidden",
    },
    dot: {
      width: 10,
      height: 10,
      borderRadius: 10,
      boxShadow: "0 0 1px 0 rgba(32, 37, 43, 0.1)",
      border: "solid 1px white",
      backgroundColor: ({ color }) =>
        ["default", "success", "error", "warning"].indexOf(color) !== -1
          ? (color === "default" && "#e8e9e9") ||
            // @ts-expect-error
            theme.palette[color].main // errors because string has no overlap
          : color,
      position: "absolute",
      right: -7,
    },
    paymentDetails: {
      display: "flex",
      fontFamily: theme.typography.fontFamily,
      justifyContent: "center",
      padding: "0 40px",
      "&:last-child": {
        "& $paymentDate": { borderRightColor: "transparent" },
      },
    },
    dateSkeleton: {
      marginLeft: "auto",
    },
  }),
);
